import React, { useContext } from 'react';
import classNames from 'classnames';
import { Trans, useTranslation } from 'react-i18next';
import { Button } from 'ncoded-component-library';
import api from 'api';
import ListingContext from '../../../../providers/Listing/Listing.context';
import useReleaseOrderStatusQuery, {
  RELEASE_ORDER_STATUS_QUERY_KEY,
} from './hooks/useReleaseOrderStatusQuery';
import { useQueryClient } from '@tanstack/react-query';
import InformationcircleIcon from 'icons/Informationcircle.icon';
import useCallbackAsync from 'hooks/useCallbackAsync';
import useGetMixpanelProps from 'hooks/useGetMixpanelProps';
import mixpanelService from 'services/mixpanelService';
import {
  PAUSED_LEAD_UNLOCK_ACTION,
  PausedLeadLocation,
  PausedLeadMixpanelProps,
  PausedLeadUnlockedEventProps,
} from 'models/MixpanelEvents';
import { getIsReleaseOrderWaitingOnBroker } from './utils';

import './ReleaseOrderPausedBanner.styles.scss';
import './ReleaseOrderPausedBanner.styles.responsive.scss';

type ReleaseOrderPausedBannerProps = {
  className?: string;
  leadId: string;
  isEnabled: boolean;
  location: PausedLeadLocation;
  mixpanelProps: Partial<PausedLeadMixpanelProps>;
  onResumed?: () => void;
  onWithdraw: () => void;
  onReply?: () => void;
};

const ReleaseOrderPausedBanner: React.FC<ReleaseOrderPausedBannerProps> = (
  props,
) => {
  const {
    className,
    leadId,
    isEnabled,
    location,
    mixpanelProps,
    onResumed,
    onWithdraw,
    onReply,
  } = props;

  const classes = classNames('release-order-paused-banner', className);

  const { t } = useTranslation();

  const { areActionsDisabled } = useContext(ListingContext);
  const queryClient = useQueryClient();

  const getMixpanelProps = useGetMixpanelProps();

  const { data: releaseOrderStatus } = useReleaseOrderStatusQuery(
    leadId,
    isEnabled,
  );

  const {
    pausedItem,
    itemsShared,
    itemsInOrder,
    isLastItem,
    isAwaitingReaction,
  } = releaseOrderStatus || {};

  const [advanceReleaseOrder, isAdvancing] = useCallbackAsync(async () => {
    const {
      data: { releaseOrder },
    } = await api.portfolio.seller.advanceReleaseOrder(leadId);

    queryClient.setQueryData(
      [RELEASE_ORDER_STATUS_QUERY_KEY, { leadId }],
      releaseOrder,
    );

    mixpanelService.pausedLeadUnlocked(
      getMixpanelProps({
        ...mixpanelProps,
        action: isLastItem
          ? PAUSED_LEAD_UNLOCK_ACTION.COMPLETE_THE_ORDER
          : PAUSED_LEAD_UNLOCK_ACTION.UNLOCK_NEXT_ITEM,
        location,
      }) as PausedLeadUnlockedEventProps,
    );

    if (!getIsReleaseOrderWaitingOnBroker(releaseOrder)) onResumed?.();
  });

  if (
    areActionsDisabled ||
    !isEnabled ||
    !getIsReleaseOrderWaitingOnBroker(releaseOrderStatus)
  )
    return null;

  return (
    <div className={classes}>
      <div className="release-order-paused-banner__message">
        <InformationcircleIcon />
        <p>
          <Trans
            i18nKey={
              isAwaitingReaction
                ? 'releaseOrderAwaitingReactionAt'
                : 'releaseOrderPausedAt'
            }
            values={{
              fileName: pausedItem?.name,
              itemsShared,
              itemsInOrder,
            }}
            components={{ b: <b /> }}
          />
        </p>
      </div>

      <div className="release-order-paused-banner__actions">
        <Button disabled={isAdvancing} onClick={advanceReleaseOrder}>
          {t(isLastItem ? 'completeTheOrder' : 'unlockNextItem')}
        </Button>

        {onReply && (
          <Button
            variant="outline"
            className="release-order-paused-banner__reply"
            onClick={onReply}
          >
            {t('replyInChat')}
          </Button>
        )}

        <Button
          variant="outline"
          className="warning-action-button"
          onClick={onWithdraw}
        >
          {t('withdrawLead')}
        </Button>
      </div>
    </div>
  );
};

export default ReleaseOrderPausedBanner;
